import React from "react";
import { Stack } from "@mui/material";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { getAuditLogsExpanded, AuditLogExpanded } from "@quest_editor/mocks/data";
import TablePanel, { AuditLogRow, AuditLogFilters } from "./TablePanel";

const AuditLog = () => {
  const { t } = useTranslation("questEditor");
  const [searchParams, setSearchParams] = useSearchParams();

  const getParamValue = (key: string) => searchParams.get(key) || "";

  const [filters, setFilters] = React.useState<AuditLogFilters>({
    domain: getParamValue("domain"),
    account: getParamValue("account"),
    promotion: getParamValue("promotion"),
    dateFrom: getParamValue("dateFrom"),
    dateTo: getParamValue("dateTo"),
  });
  const [page, setPage] = React.useState(
    Number(getParamValue("page")) || 0,
  );
  const rowsPerPage = 25;

  React.useEffect(() => {
    setFilters({
      domain: getParamValue("domain"),
      account: getParamValue("account"),
      promotion: getParamValue("promotion"),
      dateFrom: getParamValue("dateFrom"),
      dateTo: getParamValue("dateTo"),
    });
    setPage(Number(getParamValue("page")) || 0);
  }, [searchParams]);

  const auditLogs: AuditLogExpanded[] = getAuditLogsExpanded();
  const rows: AuditLogRow[] = auditLogs
    .sort((a, b) => dayjs(b.datetime).valueOf() - dayjs(a.datetime).valueOf())
    .map((al) => ({
      id: al.id,
      domain: al.domain.name,
      account: al.account.name,
      promotionId: al.promotion?.id ?? "-",
      promotionName: al.promotion?.name ?? "-",
      operation: al.operation,
      datetime: dayjs(al.datetime).format("YYYY/MM/DD HH:mm:ss"),
    }));

  const handleFilterChange = (key: keyof AuditLogFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (filters.domain) params.set("domain", filters.domain);
    if (filters.account) params.set("account", filters.account);
    if (filters.promotion) params.set("promotion", filters.promotion);
    if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
    if (filters.dateTo) params.set("dateTo", filters.dateTo);
    params.set("page", "0");
    setSearchParams(params);
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", String(newPage));
    setSearchParams(params);
    setPage(newPage);
  };

  const domain = getParamValue("domain");
  const account = getParamValue("account");
  const promotion = getParamValue("promotion");
  const dateFrom = getParamValue("dateFrom");
  const dateTo = getParamValue("dateTo");

  const filteredRows = rows.filter((row) => {
    const rowDate = dayjs(row.datetime, "YYYY/MM/DD HH:mm:ss");
    const fromDate = dateFrom ? dayjs(dateFrom).startOf("day") : null;
    const toDate = dateTo ? dayjs(dateTo).endOf("day") : null;
    return (
      (domain === "" || row.domain.includes(domain)) &&
      (account === "" || row.account.includes(account)) &&
      (promotion === "" ||
        row.promotionId.includes(promotion) ||
        row.promotionName.includes(promotion)) &&
      (!fromDate || rowDate.isAfter(fromDate) || rowDate.isSame(fromDate)) &&
      (!toDate || rowDate.isBefore(toDate) || rowDate.isSame(toDate))
    );
  });

  const start = page * rowsPerPage;
  const pageRows = filteredRows.slice(start, start + rowsPerPage);
  const pageCount = Math.ceil(filteredRows.length / rowsPerPage);

  return (
    <Stack direction="column" flexGrow={1} sx={{ flex: 1, minHeight: 0 }}>
      <TablePanel
        pageRows={pageRows}
        filteredTotal={filteredRows.length}
        filters={filters}
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
        page={page}
        pageCount={pageCount}
        onPageChange={handlePageChange}
      />

      <ActionButton
        component={Link}
        to="/quest_editor"
        sx={{ alignSelf: "flex-start", mt: 2 }}
      >
        {t("audit_log.button.back")}
      </ActionButton>
    </Stack>
  );
};
export default AuditLog;
